import React, { useEffect, useState } from "react"
import { Grid, Typography } from "@material-ui/core"

import { ChatBotFlowBlockContent } from "@/protocols/chatBot"
import { TagOption } from "@/protocols/chatBotConstructor"

import useChatBotFlowConstructorStore from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useChatBotFlowConstructorStore"
import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"

import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import TagChip from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/TagChip"

import TagDescriptionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIManageClientTagsBlock/TagDescriptionEditor"
import TagMultiSelect from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIManageClientTagsBlock/TagMultiSelect"
import { SparklesIcon } from "@/assets/icons"

import { getCurrentTagsOptions } from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"

import useStyles from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/resources/Blocks/AIManageClientTagsBlock/TagManagementSectionEditor/styles"

const AI_NOTE_TEXT = "A IA lê as mensagens do último protocolo de atendimento e atribui ao contato as tags que reconhecer entre as adicionadas acima. Pode atribuir mais de uma tag na mesma execução."

type TagManagementSectionEditorProps = {
	chatBotFlowBlockContent: ChatBotFlowBlockContent
	onSave: (chatBotFlowBlockContent: ChatBotFlowBlockContent) => void
}

const TagManagementSectionEditor: React.FC<TagManagementSectionEditorProps> = (props) => {
	const {
		chatBotFlowBlockContent,
		onSave,
		children
	} = props

	const chatBotFlowConstructorStore = useChatBotFlowConstructorStore()
	const contentEditor = useContentEditor({ chatBotFlowBlockContent })

	const [isDrawerOpen, setIsDrawerOpen] = useState(false)

	const classes = useStyles()

	const tagOptions = chatBotFlowConstructorStore.constructionResources.tagOptions || []
	const currentTagsOptions = getCurrentTagsOptions(chatBotFlowConstructorStore.constructionResources, contentEditor.content)
	const selectedTags = currentTagsOptions.tagsToAdd

	const handleChange = (tags: TagOption[] | null) => {
		const updatedActions = (tags || []).map(tag => ({
			type: "add" as const,
			tagId: tag.id
		}))

		contentEditor.changeContent({
			clientTagsManagementData: {
				actions: updatedActions
			}
		})
	}

	const handleTagRemove = (tagId: number) => {
		const remainingTags = selectedTags.filter(tag => tag.id !== tagId)
		handleChange(remainingTags)
	}

	const handleSave = () => {
		onSave(contentEditor.content)
	}

	const reloadTagOptions = async () => {
		await chatBotFlowConstructorStore.loadSpecificConstructionResources("tagOptions")
	}

	const handleDrawerOpen = () => {
		setIsDrawerOpen(true)
		reloadTagOptions()
	}

	const handleDrawerClose = () => {
		setIsDrawerOpen(false)
	}

	useEffect(() => {
		if (!isDrawerOpen) {
			return
		}

		const handleWindowFocus = () => {
			reloadTagOptions()
		}

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				reloadTagOptions()
			}
		}

		window.addEventListener("focus", handleWindowFocus)
		document.addEventListener("visibilitychange", handleVisibilityChange)

		return () => {
			window.removeEventListener("focus", handleWindowFocus)
			document.removeEventListener("visibilitychange", handleVisibilityChange)
		}
	}, [isDrawerOpen])

	const summaryText = `${selectedTags.length} ${selectedTags.length === 1 ? "tag adicionada" : "tags adicionadas"}`

	return (
		<BlockBodySectionEditor
			title="ADICIONAR TAGS"
			onSave={handleSave}
			onOpen={handleDrawerOpen}
			onClose={handleDrawerClose}
			AsideDrawerProps={{
				children: (
					<Grid
						container
						spacing={2}
					>
						<Grid
							item
							xs={12}
						>
							<TagMultiSelect
								value={selectedTags}
								onChange={handleChange}
								options={tagOptions}
								placeholder="Tags"
								getOptionSelected={(option, value) => option.id === value.id}
								noOptionsText="Sem opções"
								loadingText="Carregando..."
								renderTag={tagOption => (
									<TagChip
										color={tagOption.color}
										name={tagOption.name}
										style={{
											maxWidth: "320px",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											marginBottom: 12,
											marginRight: 12
										}}
									/>
								)}
								onUpdate={async () => {
									await chatBotFlowConstructorStore.loadSpecificConstructionResources("tagOptions")
								}}
							/>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<TagDescriptionEditor
								tags={selectedTags}
								onTagRemove={handleTagRemove}
							/>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<Grid
								container
								alignItems="center"
								justifyContent="space-between"
							>
								<Typography className={classes.summaryLabel}>
									{summaryText}
								</Typography>

								{selectedTags.length > 0 && (
									<Typography
										className={classes.limparLink}
										onClick={() => handleChange(null)}
									>
										Limpar
									</Typography>
								)}
							</Grid>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<Grid className={classes.aiNote}>
								<Grid className={classes.aiNoteIconBox}>
									<SparklesIcon className={classes.aiNoteIcon} />
								</Grid>

								<Typography className={classes.aiNoteText}>
									{AI_NOTE_TEXT}
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				)
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default TagManagementSectionEditor
